import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Button, EmptyState, GroupCard, Icon, IconButton, SkeletonCards } from "@qota/ui";
import { useNewExpense } from "../components/NewExpenseButton";
import { iconFor } from "../lib/category-icon";
import { relativeDay } from "../lib/relative-date";
import { listGroups } from "../lib/groups";
import { queryKeys } from "../lib/query-keys";
import { useSessionState } from "../lib/session-context";
import { AppHeader } from "../components/AppHeader";
import { AppNav } from "../components/AppNav";
import { MobileShell } from "../components/MobileShell";

/**
 * Schermata 01 — Gruppi.
 *
 * Ogni card risponde da sola alla domanda per cui l'app viene aperta — devo o
 * mi devono — senza entrare nel gruppo. Saldo e numero di spese arrivano dalla
 * lista in un'unica lettura, non uno per gruppo.
 *
 * Il «saldo complessivo» in cima al mockup continua a non esserci: sommare
 * saldi di gruppi diversi darebbe un numero che non corrisponde a nessun
 * debito reale verso nessuno.
 */
export function GroupsScreen() {
  const navigate = useNavigate();
  const { profile } = useSessionState();
  const newExpense = useNewExpense();

  const { data: groups, isLoading } = useQuery({
    queryKey: queryKeys.groups,
    queryFn: listGroups,
  });

  return (
    <MobileShell bottom={<AppNav value="gruppi" onAdd={newExpense.open} />}>
      <AppHeader
        title="Gruppi"
        subtitle={profile ? `Ciao, ${profile.name.split(" ")[0]}` : undefined}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/*
              Entrare in un gruppo altrui è un gesto diverso dal crearne uno, e
              fino a ora esisteva solo da sloggati: chi riceveva un codice ad
              app già aperta non aveva nessuna strada per usarlo se non chiudere
              tutto e ripartire dal benvenuto.
            */}
            <IconButton
              label="Ho un invito"
              size={36}
              onClick={() => void navigate({ to: "/invito" })}
            >
              <Icon name="ticket" size={19} />
            </IconButton>
            <IconButton
              label="Nuovo gruppo"
              size={36}
              onClick={() => void navigate({ to: "/gruppi/nuovo" })}
            >
              <Icon name="plus" size={19} />
            </IconButton>
            <Avatar name={profile?.name ?? ""} size={36} />
          </div>
        }
      />

      {/* Card, non righe: la lista dei gruppi è fatta di riquadri alti. */}
      {isLoading && <SkeletonCards count={3} />}

      {!isLoading && groups?.length === 0 && (
        <EmptyState
          icon="users"
          title="Nessun gruppo, per ora"
          body="Crea un gruppo, mettici dentro le persone con cui dividi le spese, e inizia a segnare i conti."
          action={
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <Button
                variant="accent"
                size="lg"
                iconLeft={<Icon name="plus" size={18} />}
                onClick={() => void navigate({ to: "/gruppi/nuovo" })}
              >
                Crea un gruppo
              </Button>
              {/* Chi arriva qui senza gruppi spesso non deve crearne uno: è
                  stato invitato in quello di qualcun altro. */}
              <Button
                variant="ghost"
                size="md"
                iconLeft={<Icon name="ticket" size={17} />}
                onClick={() => void navigate({ to: "/invito" })}
              >
                Ho un invito
              </Button>
            </div>
          }
        />
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--stack-gap)" }}>
        {groups?.map((group) => (
          <GroupCard
            key={group.id}
            name={group.name}
            tint={group.tint}
            members={group.memberNames.map((name) => ({ name }))}
            memberCount={group.memberCount}
            expenseCount={group.expenseCount}
            balanceCents={group.myBalanceCents}
            lastMovement={
              group.lastMovement
                ? {
                    // Un pagamento non ha categoria: l'icona è quella del
                    // trasferimento, come nella riga della cronologia.
                    icon: group.lastMovement.category
                      ? iconFor(group.lastMovement.category)
                      : "arrow-left-right",
                    label: group.lastMovement.label,
                    amountCents: group.lastMovement.amountCents,
                    when: relativeDay(group.lastMovement.occurredAt),
                  }
                : null
            }
            onClick={() => void navigate({ to: "/gruppi/$groupId", params: { groupId: group.id } })}
          />
        ))}
      </div>
      {newExpense.sheet}
    </MobileShell>
  );
}
